<template>
	<div class="space-y-56 tabletWide:space-y-112">
		<LazyHydrate when-visible>
			<layoutSection>
				<layoutPost type="wide">
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Winter Car Rentals - Hit the Slopes Safely!</h2>
				</layoutPost>
			</layoutSection>
		</LazyHydrate>
		<template v-for="(group, groupIndex) in vehicleGroups">
			<LazyHydrate :key="group.title" when-visible>
				<layoutSection>
					<layoutPost type="wide">
						<h3 class="font-bold text-24 desktop:text-32">{{ group.title }}</h3>
						<div class="text-18 desktop:text-22 space-y-24" v-html="group.content" />
					</layoutPost>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate v-for="(vehicle, vehicleIndex) in group.vehicles" :key="`${groupIndex}${vehicle.carClassId}`" when-visible>
				<vehicleLarge :vehicle="vehicle" :layoutSectionType="vehicleIndex % 2 === 0 ? 'post-grid-reversed' : 'post-grid'" :scroll-target="vehicle.carClassId" />
			</LazyHydrate>
		</template>

		<LazyHydrate when-visible>
			<layoutSection>
				<layoutPost type="wide">
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Ski Car Rental Accessories</h2>
				</layoutPost>
			</layoutSection>
		</LazyHydrate>

		<LazyHydrate when-visible>
			<layoutSection>
				<layoutPost type="wide">
					<h3 class="font-bold text-24 desktop:text-32">Ski and Snowboard Racks</h3>
					<img src="/images/blank.svg" class="object-cover lazyload" :data-src="$cloudFlare.getImage('/images/Ski and Snowboard Racks.jpg')" width="573" height="202" decoding="async" alt="Roof Rack Hire" />
					<div class="space-y-24">
						<p><nuxt-link to="/vehicles/car-rental-accessories/" class="link link-goBlue font-bold">Ski and snowboard racks</nuxt-link> are the ideal way to transport your group equipment to the snow. Our racks comfortably hold 6 pairs of skis or 4 snowboards. </p>
						<p>To hire a rack, add your itinerary details into the booking widget, select a ski rack-compatible car and add the ski rack to your reservation. Our team can fit your ski rack, so all you have to do is pick up the keys, load up your equipment and get GOing.</p>
						<p>Our ski and snowboard racks are compatible with:</p>
						<ul class="text-14 space-y-4 desktop:text-16 font-bold">
							<li v-for="vehicle in skiRacksVehicles" :key="vehicle.carClassId" class="bg-iconChevron pl-32">
								<button class="link link-goBlue font-bold" @click="scrollToVehicle(vehicle)">{{ vehicle.international }}</button>
							</li>
						</ul>
					</div>
					<div class="flex gap-16 p-16 bg-goPinkBrighter rounded-6 border border-goPink">
						<svg-icon name="info" class="iconSize-24 text-goPink" />
						<p>Please note: Ski racks and snowboards can cause damage to the boot, which is not covered under rental car insurance. We strongly advise booking a ski rack-compatible car.</p>
					</div>
				</layoutPost>
			</layoutSection>
		</LazyHydrate>

		<LazyHydrate when-visible>
			<layoutSection>
				<layoutPost type="wide">
					<h3 class="font-bold text-24 desktop:text-32">Snow Chains</h3>
					<img src="/images/blank.svg" :data-src="$cloudFlare.getImage('/images/Snow Chain.jpg')" width="356" height="356" decoding="async" alt="Roof Rack Hire" class="object-cover lazyload" />
					<div class="space-y-24">
						<p>You may not require <nuxt-link to="/vehicles/car-rental-accessories/" class="link link-goBlue font-bold">snow chains</nuxt-link>, but it is advisable to carry them in case the weather changes.</p>
						<p>To book our snow chains, add your itinerary details in the booking widget, select a ski rack-compatible car and add the snow chains to your reservation. Our team can show you <a href="/explore/blog/when-do-you-need-snow-chains/" class="link link-goBlue font-bold">how to fit snow chains</a> so you can be confident on the road.</p>
						<p>Snow chains are compatible with the following cars:</p>
						<ul class="text-14 space-y-4 desktop:text-16 font-bold">
							<li v-for="vehicle in snowChainsVehicles" :key="vehicle.carClassId" class="bg-iconChevron pl-32">
								<button class="link link-goBlue font-bold" @click="scrollToVehicle(vehicle)">{{ vehicle.international }}</button>
							</li>
						</ul>
					</div>
					<div class="flex gap-16 p-16 bg-goPinkBrighter rounded-6 border border-goPink">
						<svg-icon name="info" class="iconSize-24 text-goPink" />
						<p>Please note: Damage from snow chains is not covered under rental car insurance. Ensure you fit your snow chains correctly for the best traction on the road without causing damage to the car.</p>
					</div>
					<figure class="h-full flex items-center ">
						<videoYouTube
							class="w-full"
							title="How to fit Snow Chains"
							src="https://www.youtube.com/embed/xIns0im88Iw"
							duration="2:19"
							:image="{ mobile: { src: '/images/snow-chains-video-thumbnail.jpg' }, alt: 'Close-up of a car tire with snow chains covered in ice and snow' }"
						/>
					</figure>
				</layoutPost>
			</layoutSection>
		</LazyHydrate>
	</div>
</template>

<script>
import layoutSection from "~/components/layoutSection.vue"
import layoutPost from "~/components/layoutPost.vue"

export default {
	components: {
		layoutSection,
		layoutPost,
		vehicleLarge: () => import("~/components/vehicleLarge.vue"),
		videoYouTube: () => import('~/components/videoYouTube.vue'),
	},
	props: {
		vehicleGroups: {
			type: Array,
			required: true,
		},
		skiRacksVehicles: {
			type: Array,
			required: true,
		},
		snowChainsVehicles: {
			type: Array,
			required: true,
		},
	},
	methods: {
		scrollToVehicle(vehicle) {
			this.$scrollTo(vehicle.carClassId)
		},
	},
}
</script>
